/*
 * The scope context — recruiter-app-spec §4.2, "load-bearing, shell-level".
 *
 * §4.2's claim is that one control solves a problem that would otherwise be solved badly on every
 * screen: "recruiter wants a group view *and* an aggregate view" and "manager runs several teams".
 * Every list, board, tracker, search and dashboard reads the same setting, so this lives in the
 * shell and not in any of them.
 *
 * **What it offers is what the principal holds, and no more.** §4.2: "the selector never offers,
 * and the API never returns, a scope not held." The options come from `record_scope` on the
 * session, and the server re-derives the same answer independently — a selector that offered a
 * scope the API refuses would produce a control that fails when used, which is worse than one that
 * does not appear.
 *
 * **Three options, and the fourth is deliberately absent.** §4.2's table wants self, each group,
 * all my groups, and the whole agency. Migration 017 built the group model, so "all my groups" is
 * real: a team-scoped principal's set is the union of the groups they belong to and every
 * descendant of the groups they manage, resolved server-side on each request.
 *
 * What is *not* offered is a picker of individual groups. §4.2 wants one, and it would be a
 * different control from this one — it names a group rather than a width, so it belongs beside a
 * list rather than in the shell, and offering "Team: Delivery" here would put a specific team's
 * name on a selector that governs Home, Search and the tracker at once. Recorded rather than
 * approximated. `hiring_unit` narrows to `own` on both sides, for the reason `widestScopeFor`
 * gives: nothing may read the assignment table but the module that owns it.
 *
 * **Remembered per principal.** §4.1 asks for the nav's collapsed state to be remembered per user
 * and the same reasoning applies harder here: a recruiter who works at agency scope re-selecting
 * it on every reload would stop using the control. Keyed by membership so signing in as somebody
 * else does not inherit their view, and in `sessionStorage` beside the token — a scope is a
 * preference about a session, and it should not outlive one.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '../auth/session.js';

/**
 * The scopes a query can be answered at.
 *
 * Stated here rather than imported from the generated contract: the generated names track the
 * OpenAPI document's shape, and pinning a screen to one would make an unrelated schema-generator
 * change a compile error across the app. The API's enum is still the authority — sending a value
 * it does not know is a 422 with the accepted list in it, which is a loud failure rather than a
 * silent one.
 */
export type QueryScope = 'own' | 'team' | 'all';

interface ScopeState {
  /** What every scoped query should ask for. */
  readonly scope: QueryScope;
  /** The options this principal may choose between. One entry means: do not render a selector. */
  readonly available: readonly QueryScope[];
  readonly setScope: (next: QueryScope) => void;
}

const ScopeContext = createContext<ScopeState | null>(null);

const storageKey = (membershipId: string): string => `tf.scope.${membershipId}`;

/**
 * The scopes a principal may choose between, derived from their record scope.
 *
 * Deliberately the same shape as the server's `widestScopeFor`, and deliberately not shared code:
 * this is a browser bundle and that is a module on the API. They agree because both read the same
 * `record_scope`, and the API refusing a widened scope is what makes the agreement checkable
 * rather than assumed — `scope.test.ts` pins the server half and the browser half is one line.
 */
function scopesFor(recordScope: string): readonly QueryScope[] {
  if (recordScope === 'all') {
    return ['all', 'team', 'own'] as const;
  }
  // A team-scoped principal may narrow to their own work and no further. `hiring_unit` still
  // resolves to `own` on both sides — the assignment table it would read belongs to `clients`,
  // and no module but that one may read it.
  return recordScope === 'team' ? (['team', 'own'] as const) : (['own'] as const);
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const membershipId = me?.membership.id ?? null;
  const available = useMemo(
    () => scopesFor(me?.membership.record_scope ?? 'own'),
    [me?.membership.record_scope],
  );

  /*
   * Derived during render rather than frozen at mount.
   *
   * `useState`'s initialiser runs once, on the first render — which happens *before* the session
   * has loaded, when `record_scope` is unknown and `available` is still the safe default. An admin
   * therefore came up scoped to their own work and stayed there, because nothing recomputed it
   * when the principal arrived. Reading it every render costs nothing and cannot go stale.
   */
  const [chosen, setChosen] = useState<QueryScope | null>(null);
  const remembered = membershipId ? sessionStorage.getItem(storageKey(membershipId)) : null;

  const scope = useMemo(() => {
    const candidate = chosen ?? remembered;
    // Validated against what this principal may actually hold rather than trusted. A stored value
    // outlives a role change, and a demoted admin whose remembered `all` was replayed would send
    // a request the API answers with a 403 — a broken screen caused by a preference.
    return candidate && (available as readonly string[]).includes(candidate)
      ? (candidate as QueryScope)
      : available[0]!;
  }, [chosen, remembered, available]);

  const setScope = useCallback(
    (next: QueryScope) => {
      setChosen(next);
      if (membershipId) {
        sessionStorage.setItem(storageKey(membershipId), next);
      }
    },
    [membershipId],
  );

  const value = useMemo(
    () => ({ scope, available, setScope }),
    [scope, available, setScope],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/**
 * The current scope.
 *
 * Throws outside the provider rather than defaulting. A screen that silently got `own` because it
 * rendered outside the shell would show a recruiter their own work and a manager their own work,
 * and nobody would notice until somebody asked why the numbers were small.
 */
export function useScope(): ScopeState {
  const value = useContext(ScopeContext);
  if (!value) {
    throw new Error('useScope was called outside ScopeProvider. It is a shell-level control.');
  }
  return value;
}
